import { ThemeEvents } from '@theme/events';
import { publishStorefrontEvent, contextFor, placementFor, stableJson, onStorefrontEvent } from '@theme/analytics';

/**
 * Friction & failure detection — the centralized detector layer that sits on
 * top of the storefront analytics owner (assets/analytics.js). See
 * .claude/skills/shopify-tracking/SKILL.md § Friction detection for the full
 * taxonomy, thresholds, and exclusion rules.
 *
 * Design rules (do not regress):
 *  - ALL friction events publish through publishFriction(), which enforces
 *    per-key page caps and a global page budget on top of analytics.js's
 *    burst dedupe. High volume is not better.
 *  - Repetition signals (rapid toggles, reopens, repeated checkout clicks,
 *    filter churn, repeated errors) are derived from the already-published
 *    event stream via onStorefrontEvent — never from duplicate DOM listeners.
 *  - Components publish their own *_failed events at the failure site
 *    (quick-add, bundle swap, cart items, ...). The generic
 *    network_request_failed only fires when no specific commerce failure
 *    claimed the same window.
 *  - No user-entered content in payloads: no search queries, discount codes,
 *    field values, or free-form DOM text. Element identity comes from
 *    data-track-* attributes, tag names, and controlled attribute values.
 *  - Opt-outs: data-friction-ignore="rage-click dead-click" (space-separated
 *    list) and data-friction-success-event="<event_name>" for dead-click
 *    success signaling.
 */

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

const RAGE_CLICK_THRESHOLD = 4;
const RAGE_CLICK_WINDOW_MS = 1200;
const RAGE_CLICK_RADIUS_PX = 40;
const RAGE_CLICK_COOLDOWN_MS = 5000;

const DEAD_CLICK_WINDOW_MS = 900;

const RAPID_TOGGLE_THRESHOLD = 5; // state changes (open+close = 2)
const RAPID_TOGGLE_WINDOW_MS = 8000;
const RAPID_TOGGLE_COOLDOWN_MS = 30000;

const REOPEN_WINDOW_MS = 60000;
const REOPEN_MAX_PUBLISHES = 3;

const CHECKOUT_REPEAT_WINDOW_MS = 15000;

const FORM_ERROR_REPEAT_THRESHOLD = 2;
const FORM_ERROR_WINDOW_MS = 45000;

const FILTER_RESET_THRESHOLD = 2;
const FILTER_RESET_WINDOW_MS = 90000;
const FILTER_CHURN_THRESHOLD = 6;
const FILTER_CHURN_WINDOW_MS = 45000;

const SEARCH_REPEAT_THRESHOLD = 3;
const SEARCH_REPEAT_WINDOW_MS = 120000;

const VARIANT_CHURN_THRESHOLD = 6;
const VARIANT_CHURN_WINDOW_MS = 30000;

const DISCOUNT_REPEAT_THRESHOLD = 2;
const DISCOUNT_REPEAT_WINDOW_MS = 60000;

const NETWORK_FAIL_SETTLE_MS = 1200; // wait for a specific *_failed first
const NETWORK_FAIL_MAX_PER_PAGE = 3;

const GLOBAL_FRICTION_BUDGET = 40; // max friction events per page view

/* ------------------------------------------------------------------ */
/* Publish gate: page budget + per-key caps                            */
/* ------------------------------------------------------------------ */

let frictionPublished = 0;
/** @type {Map<string, number>} publishes per dedupe key this page */
const keyCounts = new Map();

/**
 * @param {string} name
 * @param {Object} payload
 * @param {{ key?: string, maxPerKey?: number }} [options] - key defaults to
 *   the event name; maxPerKey defaults to 1 (once per key per page view).
 */
function publishFriction(name, payload, options = {}) {
  if (frictionPublished >= GLOBAL_FRICTION_BUDGET) return false;
  const key = `${name}|${options.key || ''}`;
  const max = options.maxPerKey ?? 1;
  const count = keyCounts.get(key) || 0;
  if (count >= max) return false;
  keyCounts.set(key, count + 1);
  frictionPublished += 1;
  return publishStorefrontEvent(name, payload);
}

/* ------------------------------------------------------------------ */
/* Safe element identification                                         */
/* ------------------------------------------------------------------ */

/**
 * Controlled element description — attribute values and tag names only,
 * never free-form text.
 * @param {Element} element
 */
function describeElement(element) {
  const trackHost = element.closest('[data-track-component]');
  // Nearest custom-element ancestor is a good component name in this theme
  // (cart-drawer-component, quick-add-component, buy-in-bulk-modal, ...).
  let component = trackHost?.getAttribute('data-track-component') || null;
  if (!component) {
    let node = element;
    while (node && node !== document.body) {
      if (node.tagName?.includes('-')) {
        component = node.tagName.toLowerCase();
        break;
      }
      node = node.parentElement;
    }
  }

  const control = element.closest('button, a[href], [role="button"], summary, input, select') || element;
  const action =
    control.getAttribute?.('data-track-action') ||
    control.getAttribute?.('on:click') ||
    control.getAttribute?.('data-analytics-click') ||
    null;

  return {
    component: component || control.tagName?.toLowerCase() || 'unknown',
    element_id: control.id || null,
    element_role: control.getAttribute?.('role') || control.tagName?.toLowerCase() || null,
    element_action: action,
    section_id: element.closest('.shopify-section')?.id || null,
    block_id: element.closest('[data-block-id]')?.getAttribute('data-block-id') || null,
    placement: placementFor(element),
  };
}

/** @param {Element} element @param {string} kind */
function frictionIgnored(element, kind) {
  const host = element.closest('[data-friction-ignore]');
  if (!host) return false;
  const kinds = (host.getAttribute('data-friction-ignore') || '').split(/\s+/);
  return kinds.includes(kind) || kinds.includes('all');
}

/* ------------------------------------------------------------------ */
/* Time-window counter                                                 */
/* ------------------------------------------------------------------ */

class WindowCounter {
  /** @type {Map<string, number[]>} */
  #hits = new Map();
  /** @type {Map<string, number>} */
  #cooldownUntil = new Map();

  constructor(windowMs, threshold, cooldownMs = 0) {
    this.windowMs = windowMs;
    this.threshold = threshold;
    this.cooldownMs = cooldownMs;
  }

  /**
   * Records a hit; returns {count, fired} where fired is true exactly when
   * the threshold is crossed outside a cooldown.
   * @param {string} key
   */
  hit(key) {
    const now = Date.now();
    const hits = (this.#hits.get(key) || []).filter((t) => now - t < this.windowMs);
    hits.push(now);
    this.#hits.set(key, hits);
    if (hits.length < this.threshold) return { count: hits.length, fired: false };
    if ((this.#cooldownUntil.get(key) || 0) > now) return { count: hits.length, fired: false };
    this.#cooldownUntil.set(key, now + this.cooldownMs);
    this.#hits.set(key, []);
    return { count: hits.length, fired: true };
  }

  reset(key) {
    this.#hits.delete(key);
  }
}

/* ------------------------------------------------------------------ */
/* Cart request observation (fetch)                                    */
/* ------------------------------------------------------------------ */

// Observes /cart/*.js requests for (a) checkout_click_repeated's
// cart_request_pending flag, (b) dead-click success signaling, and (c) the
// generic network_request_failed fallback. The wrapper is passive: it never
// alters arguments, responses, or errors.
let pendingCartRequests = 0;
let lastCartRequestAt = 0;
let lastSpecificFailureAt = 0;
let networkFailuresPublished = 0;

const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?/;

/** Called by component-level *_failed publishes so the generic stays quiet. */
function recordSpecificFailure() {
  lastSpecificFailureAt = Date.now();
}

function operationFromUrl(url) {
  const match = CART_URL_RE.exec(url);
  return match ? match[1] : 'cart';
}

function reportNetworkFailure(url, status, errorType) {
  const startedAt = Date.now();
  window.setTimeout(() => {
    // A component already published a precise *_failed for this window.
    if (lastSpecificFailureAt >= startedAt - 500) return;
    if (networkFailuresPublished >= NETWORK_FAIL_MAX_PER_PAGE) return;
    networkFailuresPublished += 1;
    publishFriction(
      'network_request_failed',
      {
        operation: operationFromUrl(url),
        response_status: status || null,
        error_type: errorType,
      },
      { key: `${operationFromUrl(url)}|${errorType}`, maxPerKey: 2 }
    );
  }, NETWORK_FAIL_SETTLE_MS);
}

const originalFetch = window.fetch;
if (typeof originalFetch === 'function') {
  window.fetch = function (input, init) {
    let url = '';
    try {
      url = typeof input === 'string' ? input : input?.url || '';
    } catch {
      // never interfere
    }
    const isCartRequest = CART_URL_RE.test(url);
    if (isCartRequest) {
      pendingCartRequests += 1;
      lastCartRequestAt = Date.now();
    }
    const result = originalFetch.apply(this, arguments);
    if (isCartRequest && result?.then) {
      result.then(
        (response) => {
          pendingCartRequests = Math.max(0, pendingCartRequests - 1);
          if (response && !response.ok && response.status >= 500) {
            reportNetworkFailure(url, response.status, 'server');
          }
        },
        () => {
          pendingCartRequests = Math.max(0, pendingCartRequests - 1);
          reportNetworkFailure(url, null, 'network');
        }
      );
    }
    return result;
  };
}

/* ------------------------------------------------------------------ */
/* Rage clicks                                                         */
/* ------------------------------------------------------------------ */

// Repetition-is-expected controls: steppers, carousel/gallery arrows, media
// scrubbing, range sliders.
const RAGE_EXCLUDE_SELECTOR = [
  '.quantity-selector',
  'quantity-selector-component',
  'cart-quantity-selector-component',
  '.buy-in-bulk-modal__stepper',
  '[data-quantity-increase]',
  '[data-quantity-decrease]',
  '.cart-recommendations__arrow',
  '.media-gallery__arrow',
  '.slideshow-controls',
  'slideshow-arrows',
  '[data-progress]',
  '[data-playpause]',
  'input[type="range"]',
  'video',
  'audio',
  '.year-range__slider',
].join(', ');

/** @type {Array<{x: number, y: number, t: number}>} */
let recentClicks = [];
let rageCooldownUntil = 0;

function detectRageClick(event, target) {
  const now = Date.now();
  recentClicks = recentClicks.filter((click) => now - click.t < RAGE_CLICK_WINDOW_MS);
  recentClicks.push({ x: event.clientX, y: event.clientY, t: now });

  if (now < rageCooldownUntil) return;

  const cluster = recentClicks.filter(
    (click) => Math.hypot(click.x - event.clientX, click.y - event.clientY) <= RAGE_CLICK_RADIUS_PX
  );
  if (cluster.length < RAGE_CLICK_THRESHOLD) return;
  if (target.closest(RAGE_EXCLUDE_SELECTOR)) return;
  if (frictionIgnored(target, 'rage-click')) return;

  rageCooldownUntil = now + RAGE_CLICK_COOLDOWN_MS;
  recentClicks = [];

  const description = describeElement(target);
  publishFriction(
    'rage_click_detected',
    {
      ...description,
      click_count: cluster.length,
      time_window_ms: RAGE_CLICK_WINDOW_MS,
    },
    { key: `${description.component}|${description.element_action}`, maxPerKey: 2 }
  );
}

/* ------------------------------------------------------------------ */
/* Dead clicks                                                         */
/* ------------------------------------------------------------------ */

// Candidates: controls that promise behavior. Plain links are excluded (their
// success is navigation, often after this page's lifetime); form fields,
// labels and native-behavior elements are excluded (their result is
// invisible to us but real).
const DEAD_CANDIDATE_SELECTOR = 'button, [role="button"], [on\\:click]';
const DEAD_EXCLUDE_SELECTOR = [
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'a[href]',
  '[disabled]',
  '[aria-disabled="true"]',
  '[aria-busy="true"]',
  'copy-discount-code',
  'copy-to-clipboard-component',
  '[data-progress]',
  '[data-playpause]',
  '.slideshow-controls',
  'comparison-slider-component',
].join(', ');

/** One pending observation at a time keeps the cost of this detector flat. */
let pendingDeadClick = false;

function detectDeadClick(event, target) {
  const control = target.closest(DEAD_CANDIDATE_SELECTOR);
  if (!control || !(control instanceof Element)) return;
  if (control.matches(DEAD_EXCLUDE_SELECTOR) || control.closest(DEAD_EXCLUDE_SELECTOR)) return;
  // Repetition-is-expected controls (steppers, arrows, scrubbers) often
  // change only element properties, which a MutationObserver can't see —
  // excluding them prevents false positives AND stops them starving the
  // single observation slot.
  if (control.closest(RAGE_EXCLUDE_SELECTOR)) return;
  if (frictionIgnored(control, 'dead-click')) return;
  if (window.getSelection?.()?.toString()) return; // text selection, not a click
  if (pendingDeadClick) return; // one observation at a time keeps this cheap

  const requiredEvent = control.closest('[data-friction-success-event]')?.getAttribute('data-friction-success-event');
  const componentRoot = control.closest('[data-track-component]') || findComponentRoot(control) || control;
  const startUrl = window.location.href;
  const startFocus = document.activeElement;
  const startCartActivity = lastCartRequestAt;
  let succeeded = false;
  const succeed = () => {
    succeeded = true;
  };

  // Success signal 1: any storefront analytics event (or the specific one the
  // element names).
  const unsubscribeMarker = (name) => {
    if (requiredEvent ? name === requiredEvent : true) succeed();
  };
  deadClickEventProbes.push(unsubscribeMarker);

  // Success signal 2: DOM mutation inside the clicked component.
  const observer = new MutationObserver(succeed);
  try {
    observer.observe(componentRoot, { childList: true, subtree: true, attributes: true });
  } catch {
    // ignore
  }

  window.setTimeout(() => {
    cleanup();
    // Success signal 3-6, checked at deadline: navigation, focus transfer,
    // cart request initiated, in-flight request.
    if (
      succeeded ||
      window.location.href !== startUrl ||
      document.activeElement !== startFocus ||
      lastCartRequestAt !== startCartActivity ||
      pendingCartRequests > 0
    ) {
      return;
    }
    const description = describeElement(control);
    publishFriction(
      'dead_click_detected',
      { ...description, time_window_ms: DEAD_CLICK_WINDOW_MS },
      { key: `${description.component}|${description.element_id}|${description.element_action}|${description.section_id}` }
    );
  }, DEAD_CLICK_WINDOW_MS);

  function cleanup() {
    observer.disconnect();
    const index = deadClickEventProbes.indexOf(unsubscribeMarker);
    if (index !== -1) deadClickEventProbes.splice(index, 1);
    pendingDeadClick = false;
  }
  pendingDeadClick = true;
}

/** @type {Array<(name: string) => void>} */
const deadClickEventProbes = [];

/** Nearest Horizon component/custom-element root for scoped observation. */
function findComponentRoot(element) {
  let node = element;
  while (node && node !== document.body) {
    if (node.tagName?.includes('-') || node.classList?.contains('shopify-section')) return node;
    node = node.parentElement;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Click entry point                                                   */
/* ------------------------------------------------------------------ */

// Listens on WINDOW capture, which runs before analytics.js's document-level
// capture handlers — so the dead-click success probe is registered before the
// same click's own data-analytics-click publish fires (which must count as
// the success signal, not be missed).
window.addEventListener(
  'click',
  (event) => {
    // Keyboard "clicks" (Enter/Space) report detail 0 — repetition there is
    // navigation, not rage.
    if (!event.isTrusted || event.detail === 0) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    detectRageClick(event, target);
    detectDeadClick(event, target);
  },
  { capture: true }
);

/* ------------------------------------------------------------------ */
/* Event-stream derived signals                                        */
/* ------------------------------------------------------------------ */

const toggleCounter = new WindowCounter(RAPID_TOGGLE_WINDOW_MS, RAPID_TOGGLE_THRESHOLD, RAPID_TOGGLE_COOLDOWN_MS);
const checkoutCounter = new WindowCounter(CHECKOUT_REPEAT_WINDOW_MS, 2);
const formErrorCounter = new WindowCounter(FORM_ERROR_WINDOW_MS, FORM_ERROR_REPEAT_THRESHOLD);
const filterResetCounter = new WindowCounter(FILTER_RESET_WINDOW_MS, FILTER_RESET_THRESHOLD);
const filterChurnCounter = new WindowCounter(FILTER_CHURN_WINDOW_MS, FILTER_CHURN_THRESHOLD);
const searchCounter = new WindowCounter(SEARCH_REPEAT_WINDOW_MS, SEARCH_REPEAT_THRESHOLD);
const variantCounter = new WindowCounter(VARIANT_CHURN_WINDOW_MS, VARIANT_CHURN_THRESHOLD);
const discountCounter = new WindowCounter(DISCOUNT_REPEAT_WINDOW_MS, DISCOUNT_REPEAT_THRESHOLD);

/** @type {Map<string, {opens: number, lastClosedAt: number, published: number}>} */
const dialogHistory = new Map();

const TOGGLE_SUFFIX_RE = /_(expanded|collapsed|opened|closed)$/;

onStorefrontEvent((name, payload) => {
  // Dead-click success probes
  for (const probe of [...deadClickEventProbes]) probe(name);

  // ---- rapid toggles (accordions, dialogs, expanders) ----
  const suffixMatch = TOGGLE_SUFFIX_RE.exec(name);
  if (suffixMatch) {
    const base = name.slice(0, suffixMatch.index);
    const toggleKey = `${base}|${payload.label || ''}|${payload.block_id || payload.section_id || ''}`;
    const { count, fired } = toggleCounter.hit(toggleKey);
    if (fired) {
      publishFriction(
        'rapid_toggle_detected',
        {
          component: base,
          control_name: payload.label || null,
          toggle_count: count,
          final_state: suffixMatch[1],
          time_window_ms: RAPID_TOGGLE_WINDOW_MS,
          section_id: payload.section_id || null,
          placement: payload.placement || null,
        },
        { key: toggleKey }
      );
    }
  }

  // ---- modal / drawer reopens ----
  if (name.endsWith('_opened')) {
    const base = name.slice(0, -'_opened'.length);
    const entry = dialogHistory.get(base) || { opens: 0, lastClosedAt: 0, published: 0 };
    entry.opens += 1;
    const sinceClose = entry.lastClosedAt ? Date.now() - entry.lastClosedAt : null;
    if (entry.opens > 1 && sinceClose !== null && sinceClose < REOPEN_WINDOW_MS && entry.published < REOPEN_MAX_PUBLISHES) {
      entry.published += 1;
      const eventName =
        base === 'quick_add' ? 'quick_add_reopened' : base.includes('drawer') ? 'drawer_reopened' : 'modal_reopened';
      publishFriction(
        eventName,
        {
          component: base,
          reopen_count: entry.opens - 1,
          time_since_close_ms: sinceClose,
          placement: payload.placement || null,
          section_id: payload.section_id || null,
        },
        { key: base, maxPerKey: REOPEN_MAX_PUBLISHES }
      );
    }
    dialogHistory.set(base, entry);
  } else if (name.endsWith('_closed')) {
    const base = name.slice(0, -'_closed'.length);
    const entry = dialogHistory.get(base) || { opens: 0, lastClosedAt: 0, published: 0 };
    entry.lastClosedAt = Date.now();
    dialogHistory.set(base, entry);
  }

  // A cart mutation is a completed task: reopening the cart drawer after it
  // is normal shopping, not friction.
  if (name === 'add_to_cart' || name === 'cart_quantity_changed' || name === 'remove_from_cart') {
    dialogHistory.delete('cart_drawer');
  }

  // ---- repeated checkout clicks ----
  if (name === 'checkout_clicked') {
    const { count, fired } = checkoutCounter.hit('checkout');
    if (fired) {
      publishFriction('checkout_click_repeated', {
        click_count: count,
        component: 'checkout_button',
        placement: payload.placement || null,
        cart_item_count: payload.item_count ?? null,
        cart_request_pending: pendingCartRequests > 0,
        time_window_ms: CHECKOUT_REPEAT_WINDOW_MS,
      });
    }
  }

  // ---- repeated form errors (cart/product forms) ----
  if (name === 'cart_error') {
    const key = payload.source || 'cart';
    const { count, fired } = formErrorCounter.hit(key);
    if (fired) {
      publishFriction(
        'form_error_repeated',
        {
          form_type: 'product_form',
          error_type: 'server_rejected',
          attempt_count: count,
          placement: payload.placement || null,
        },
        { key }
      );
    }
  }

  // ---- filter churn ----
  if (name === 'filter_cleared') {
    const { count, fired } = filterResetCounter.hit('reset');
    if (fired) {
      publishFriction('filter_reset_repeated', { reset_count: count, time_window_ms: FILTER_RESET_WINDOW_MS });
    }
  }
  if (name === 'filter_applied' || name === 'filter_removed' || name === 'filter_cleared') {
    const { count, fired } = filterChurnCounter.hit('churn');
    if (fired) {
      publishFriction('filter_repeatedly_changed', { change_count: count, time_window_ms: FILTER_CHURN_WINDOW_MS });
    }
  }

  // ---- repeated searches (no terms in payload) ----
  if (name === 'search_submitted') {
    const { count, fired } = searchCounter.hit('search');
    if (fired) {
      publishFriction('search_repeated', { attempt_count: count, time_window_ms: SEARCH_REPEAT_WINDOW_MS });
    }
  }

  // ---- variant churn (per product) ----
  if (name === 'variant_selected') {
    const key = String(payload.product_id || 'unknown');
    const { count, fired } = variantCounter.hit(key);
    if (fired) {
      publishFriction(
        'variant_selection_repeated',
        {
          product_id: payload.product_id || null,
          selection_count: count,
          time_window_ms: VARIANT_CHURN_WINDOW_MS,
          placement: payload.placement || null,
        },
        { key }
      );
    }
  }

  // ---- repeated rejected discount codes (codes never in payload) ----
  if (name === 'discount_rejected') {
    const { count, fired } = discountCounter.hit('discount');
    if (fired) {
      publishFriction('discount_code_repeated', { attempt_count: count, time_window_ms: DISCOUNT_REPEAT_WINDOW_MS });
    }
  }

  // Specific commerce failures silence the generic network fallback.
  if (name.endsWith('_failed') && name !== 'network_request_failed') {
    recordSpecificFailure();
  }
});

/* ------------------------------------------------------------------ */
/* Commerce failure bridges (theme event bus)                          */
/* ------------------------------------------------------------------ */

// product-form dispatches CartAddEvent with didError:true when the add is
// rejected (max quantity, sold out mid-session). It ALSO dispatches
// cart:error for the same failure — the didError branch is the specific one,
// so the cart:error handler skips failures product-form just claimed.
let lastAddFailureAt = 0;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = event.detail?.data || {};
  if (!data.didError) return;
  const target = event.target instanceof Element ? event.target : null;
  lastAddFailureAt = Date.now();
  recordSpecificFailure();
  publishFriction(
    'add_to_cart_failed',
    {
      ...contextFor(target),
      product_id: data.productId || null,
      quantity: data.itemCount || null,
      component: data.source || null,
      operation: 'add',
      error_type: 'server_rejected',
      placement: placementFor(target),
    },
    { key: String(data.productId || data.source || 'add'), maxPerKey: 2 }
  );
});

document.addEventListener(ThemeEvents.cartError, (event) => {
  // Within the same tick as a product-form didError add → already reported.
  if (Date.now() - lastAddFailureAt < 500) return;
  recordSpecificFailure();
  publishFriction(
    'cart_update_failed',
    {
      component: event.detail?.sourceId || null,
      operation: 'update',
      error_type: 'server_rejected',
    },
    { key: String(event.detail?.sourceId || 'cart'), maxPerKey: 2 }
  );
});
